/**
 * #728 — each trader's own CFB landing board, stored PER AUTHENTICATED USER.
 *
 * The CFB slate is large (a full prod-hydra pull on 2026-08-20 returned 184
 * games in one day), so some traders want the board to open on FBS while
 * others want the complete slate. This is that choice. The unset value is
 * `'cfb'`, the whole slate, so a trader who never opens Settings sees exactly
 * the #653 behaviour.
 *
 * ── Why this is read at ENTRY, never during render ──────────────────────
 *
 * `?sport=cfb` means two things at once: "no bucket chosen" AND the explicit
 * `All CFB` chip. A preference applied while rendering could not tell them
 * apart, so it would narrow the All CFB chip and every `?sport=cfb` deep link
 * along with the case it means to serve.
 *
 * Resolving at entry removes the ambiguity instead of arbitrating it: the two
 * moments that would otherwise land on the bare `'cfb'` key consult this value
 * and navigate to a real board key, after which the URL is always literal.
 * A bucket chip, a deep link, a focused market, and a live update all mean
 * exactly what they say. See ValueDashboard's mount snapshot and
 * `handleSportToggle` for the two callers.
 *
 * ── Why a module and not a store ────────────────────────────────────────
 *
 * The sibling per-user stores (`savedGames`, `minimizedGames`, `liveHotkeys`)
 * hold lists and maps read continuously during render, which is what forces
 * their in-memory state, ownership gate, `hydrateFor`, and `purgeUserCaches`
 * entry. One enum read at two entry moments needs none of that: every read
 * names the user it is for, so there is no cached value to leak across a
 * logout and no hydration lag to gate against. The shape follows the
 * preference family it actually belongs to — `lib/audio/soundSettings`,
 * `lib/alerts/alertPreferences`, `lib/infra/themeManager` — where a plain data
 * module pairs with a thin `hooks/ui` hook that owns cross-tab sync.
 */
import { storageKey } from '@/lib/infra/storage';
import { CFB_BUCKET_DASHBOARD_SPORTS, type CfbBucketDashboardSport } from './dashboardSport';

/** The board a clean CFB entry opens on. `'cfb'` is the whole slate — both the
 *  factory default and a legal explicit choice, which is why it is stored the
 *  same way as the buckets rather than as an absence. */
export type CfbLandingBoard = 'cfb' | CfbBucketDashboardSport;

/** The whole slate — what an unset, unreadable, or unrecognized value means. */
export const DEFAULT_CFB_LANDING_BOARD: CfbLandingBoard = 'cfb';

/** What a stored value may say. Derived from the routing keys rather than
 *  restated, so a bucket added to the board is legal to store the same day.
 *  The trader-facing list of the same four boards, with their labels, is
 *  `CFB_BOARD_TABS` — this set deliberately carries no presentation. */
const LEGAL: ReadonlySet<string> = new Set<string>([
  DEFAULT_CFB_LANDING_BOARD,
  ...CFB_BUCKET_DASHBOARD_SPORTS,
]);

/** Exported so the hook can filter `storage` events down to this user's key
 *  rather than re-reading on every unrelated write in the app. */
export function cfbLandingStorageKey(userId: string): string {
  return storageKey(`galactus:cfbLandingBoard:${userId}`);
}

/**
 * The board `userId` has chosen for CFB, or the whole slate.
 *
 * Signed out reads as the default rather than as an error: the dashboard is
 * reachable mid-hydration, and falling back to today's behaviour is both the
 * safe direction and the one no trader can be surprised by.
 */
export function readCfbLandingBoard(userId: string | null | undefined): CfbLandingBoard {
  if (!userId) return DEFAULT_CFB_LANDING_BOARD;
  try {
    const raw = localStorage.getItem(cfbLandingStorageKey(userId));
    // A value written by a future build, or corrupted in place, is not worth
    // distinguishing from an absent one — both mean "no board I can honour".
    return raw && LEGAL.has(raw) ? (raw as CfbLandingBoard) : DEFAULT_CFB_LANDING_BOARD;
  } catch {
    // Unavailable localStorage (private mode) — the shipped behaviour.
    return DEFAULT_CFB_LANDING_BOARD;
  }
}

/**
 * Record `board` as `userId`'s CFB landing choice.
 *
 * Best-effort, matching the sibling preference modules: a full or unavailable
 * localStorage leaves the session working on the caller's own state rather
 * than throwing out of a settings click.
 */
export function writeCfbLandingBoard(userId: string | null | undefined, board: CfbLandingBoard): void {
  if (!userId) return;
  try {
    localStorage.setItem(cfbLandingStorageKey(userId), board);
  } catch {
    // Full or unavailable localStorage — nothing else to do.
  }
}
